import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Domain as DomainIcon,
  Code as CodeIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import apiClient from '../../services/api';

const TenantManagementPageOntology = () => {
  const { isAdmin, currentUser } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    totalCodes: 0,
    totalFiles: 0
  });
  
  const [tenantForm, setTenantForm] = useState({
    tenant_name: '',
    description: '',
    domain: '',
    primary_contact_name: '',
    primary_contact_email: '',
    bsda_time: '',
    is_active: true,
    agent_emoji: '🏢',
    agent_bucket: '',  // 비워두면 hyperlounge-{tenant_code}-foldersync 자동 추론
    provision_agent: true,
    tenant_codes: [
      { tenant_code: '', description: '', is_primary: true, action: 'add' }
    ]
  });

  // Ontology API를 사용하여 테넌트 로드 (RESTful)
  useEffect(() => {
    if (isAdmin()) {
      loadTenants();
    }
  }, [isAdmin]);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAllTenants();
      
      if (response && response.success) {
        const tenantList = response.data || [];
        setTenants(tenantList);
        
        // 통계 계산
        const activeTenants = tenantList.filter(t => t.is_active);
        const totalCodes = tenantList.reduce(
          (sum, t) => sum + (t.tenant_codes?.length || 0), 0
        );
        const totalFiles = tenantList.reduce(
          (sum, t) => sum + (t.file_count || 0), 0
        );
        
        setStats({
          totalTenants: tenantList.length,
          activeTenants: activeTenants.length,
          totalCodes: totalCodes,
          totalFiles: totalFiles
        });
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('테넌트 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setTenantForm({
      tenant_name: '',
      description: '',
      domain: '',
      primary_contact_name: '',
      primary_contact_email: '',
      bsda_time: '',
      is_active: true,
      agent_emoji: '🏢',
      agent_bucket: '',
      provision_agent: true,
      tenant_codes: [
        { tenant_code: '', description: '', is_primary: true, action: 'add' }
      ]
    });
    setDialogOpen(true);
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      tenant_name: tenant.tenant_name || '',
      description: tenant.description || '',
      domain: tenant.domain || '',
      primary_contact_name: tenant.primary_contact_name || '',
      primary_contact_email: tenant.primary_contact_email || '',
      bsda_time: tenant.bsda_time || '',
      is_active: tenant.is_active !== false,
      agent_emoji: tenant.agent_emoji || '🏢',
      provision_agent: false,  // 수정 시에는 K8s 재생성 안 함
      tenant_codes: tenant.tenant_codes?.map(tc => ({
        ...tc,
        action: 'existing'
      })) || []
    });
    setDialogOpen(true);
  };

  const handleAddTenantCode = () => {
    setTenantForm(prev => ({
      ...prev,
      tenant_codes: [
        ...prev.tenant_codes,
        { 
          tenant_code: '', 
          description: '', 
          is_primary: false, 
          action: 'add' 
        }
      ]
    }));
  };

  const handleRemoveTenantCode = (index) => {
    setTenantForm(prev => {
      const updatedCodes = [...prev.tenant_codes];
      if (updatedCodes[index].id) {
        // 기존 코드는 삭제 마크만
        updatedCodes[index].action = 'delete';
      } else {
        // 새로 추가된 코드는 배열에서 제거
        updatedCodes.splice(index, 1);
      }
      return { ...prev, tenant_codes: updatedCodes };
    });
  };

  const handleTenantCodeChange = (index, field, value) => {
    setTenantForm(prev => {
      const updatedCodes = [...prev.tenant_codes];
      updatedCodes[index][field] = value;
      if (updatedCodes[index].id && updatedCodes[index].action === 'existing') {
        updatedCodes[index].action = 'update';
      }
      return { ...prev, tenant_codes: updatedCodes };
    });
  };

  const handleSaveTenant = async () => {
    if (!tenantForm.tenant_name) {
      toast.error('테넌트 이름은 필수입니다.');
      return;
    }

    const activeCodes = tenantForm.tenant_codes.filter(tc => tc.action !== 'delete');
    if (activeCodes.length === 0) {
      toast.error('최소 하나의 테넌트 코드가 필요합니다.');
      return;
    }

    // 모든 코드는 tenant_code가 필수 (사용자가 직접 입력)
    const codesWithoutCode = activeCodes.filter(tc => !tc.tenant_code || tc.tenant_code.trim() === '');
    if (codesWithoutCode.length > 0) {
      toast.error('모든 테넌트 코드를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      if (editingTenant) {
        // 테넌트 수정 — DB 만
        await apiClient.updateTenant(editingTenant.id, {
          ...tenantForm,
          updated_by: currentUser?.email || 'admin'
        });
        toast.success('테넌트가 수정되었습니다.');
      } else {
        // 새 테넌트 생성 — DB + (선택) K8s agent
        const createData = {
          ...tenantForm,
          created_by: currentUser?.email || 'admin'
        };
        console.log('🔍 [Frontend] Creating tenant with data:', JSON.stringify(createData, null, 2));
        await apiClient.createTenant(createData);

        if (tenantForm.provision_agent) {
          // 활성 코드 별로 K8s 에이전트 생성 (api-gateway 는 id 를 7자리 hex 로 검증)
          const agentResults = [];
          for (const tc of activeCodes) {
            const code8 = tc.tenant_code;
            const id7 = code8.slice(0, 7);
            const bucket = tenantForm.agent_bucket
              ? tenantForm.agent_bucket.trim()
              : `hyperlounge-${code8}-foldersync`;
            const payload = {
              id: id7,
              name: tenantForm.tenant_name,
              nameKorean: tenantForm.tenant_name,
              bucket: bucket,
              identity: {
                name: `${tenantForm.tenant_name} AI`,
                emoji: tenantForm.agent_emoji || '🏢',
              },
              enabled: true,
            };
            try {
              await apiClient.createTenantAgent(payload);
              agentResults.push({ code: id7, ok: true });
            } catch (err) {
              agentResults.push({ code: id7, ok: false, error: err.message });
            }
          }

          const okCount = agentResults.filter(r => r.ok).length;
          const failResults = agentResults.filter(r => !r.ok);
          if (failResults.length === 0) {
            toast.success(`테넌트 + 에이전트 ${okCount}개 생성 완료`);
          } else {
            const failMsg = failResults
              .map((r) => `${r.code}: ${r.error.split('\n')[0]}`)
              .join(' / ');
            toast.error(
              `테넌트는 생성됨. 에이전트 ${okCount}개 성공, ${failResults.length}개 실패 — ${failMsg}`
            );
          }
        } else {
          toast.success('테넌트가 생성되었습니다. (에이전트는 별도)');
        }
      }

      setDialogOpen(false);
      await loadTenants();

      // 다른 컴포넌트에 테넌트 업데이트 알림
      window.dispatchEvent(new CustomEvent('tenantsUpdated'));
    } catch (error) {
      console.error('Error saving tenant:', error);
      const errorMessage = error.response?.data?.error || error.message || '테넌트 저장에 실패했습니다.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    // 활성 tenant_code 추출 (api-gateway 는 7자리 hex 검증 → slice 0..7)
    const activeCodes = (tenant.tenant_codes || [])
      .filter((tc) => tc.action !== 'delete' && tc.tenant_code)
      .map((tc) => tc.tenant_code.slice(0, 7));

    const codesPreview = activeCodes.length
      ? activeCodes.join(', ')
      : '(없음 — DB 만 정리)';

    const confirmMessage =
      `정말로 이 테넌트를 삭제하시겠습니까?\n\n` +
      `테넌트: ${tenant.tenant_name || tenant.id}\n` +
      `삭제 대상 에이전트 코드: ${codesPreview}\n\n` +
      `처리 내용:\n` +
      `① K8s 에이전트 파드(Deployment + Service + ConfigMap) 제거 → 변환 중단\n` +
      `② Ontology DB 의 테넌트 비활성화\n\n` +
      `⚠️ GCS 버킷의 원본/HAI 데이터는 보존됩니다.\n` +
      `   (해지 후 데이터 정리는 별도 절차로 운영)`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    const agentResults = []; // [{ code, ok, error }]
    let dbOk = false;

    try {
      // ① K8s 에이전트 삭제 — 코드별 병렬 (실패해도 계속)
      await Promise.all(
        activeCodes.map(async (code) => {
          try {
            await apiClient.deleteTenantAgent(code);
            agentResults.push({ code, ok: true });
          } catch (err) {
            agentResults.push({ code, ok: false, error: err.message });
          }
        })
      );

      // ② Ontology DB 비활성화 (K8s 결과와 독립적으로 진행)
      try {
        await apiClient.deleteTenant(tenant.id);
        dbOk = true;
      } catch (err) {
        console.error('DB delete failed:', err);
      }

      // 결과 통합 toast
      const okCodes = agentResults.filter((r) => r.ok).map((r) => r.code);
      const failCodes = agentResults.filter((r) => !r.ok);

      if (failCodes.length === 0 && dbOk) {
        toast.success(
          activeCodes.length
            ? `테넌트 삭제 완료 (에이전트 ${okCodes.length}개 + DB)`
            : '테넌트가 비활성화되었습니다.'
        );
      } else {
        const parts = [];
        if (okCodes.length) parts.push(`✅ 에이전트 ${okCodes.length}개 제거`);
        if (failCodes.length) {
          parts.push(
            `❌ 에이전트 실패 ${failCodes.length}개: ${failCodes
              .map((f) => `${f.code}(${f.error.split('\n')[0]})`)
              .join(', ')}`
          );
        }
        parts.push(dbOk ? '✅ DB 비활성화' : '❌ DB 비활성화 실패');
        toast.error(parts.join(' / '));
      }

      await loadTenants();
      window.dispatchEvent(new CustomEvent('tenantsUpdated'));
    } catch (error) {
      console.error('Error deleting tenant (unexpected):', error);
      toast.error(`테넌트 삭제 중 예기치 못한 오류: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          관리자 권한이 필요합니다.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon />
            테넌트 관리 (Ontology API)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            온톨로지 데이터 대시보드 - 고객사별 테넌트 관리 (멀티 코드 지원)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTenant}
          sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
        >
          테넌트 생성
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                총 테넌트
              </Typography>
              <Typography variant="h4">
                {stats.totalTenants}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                활성 테넌트
              </Typography>
              <Typography variant="h4">
                {stats.activeTenants}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                총 테넌트 코드
              </Typography>
              <Typography variant="h4">
                {stats.totalCodes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tenants Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>테넌트명</TableCell>
                <TableCell>테넌트 코드</TableCell>
                <TableCell>도메인</TableCell>
                <TableCell>주요 담당자</TableCell>
                <TableCell>담당자 이메일</TableCell>
                <TableCell>파일 수</TableCell>
                <TableCell>상태</TableCell>
                <TableCell>작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {tenant.tenant_name}
                      </Typography>
                      {tenant.description && (
                        <Typography variant="caption" color="text.secondary">
                          {tenant.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {tenant.tenant_codes
                        ?.filter((code, index, array) => 
                          // 중복 제거: 같은 tenant_code가 있으면 첫 번째만 남김
                          array.findIndex(c => c.tenant_code === code.tenant_code) === index
                        )
                        ?.map((code) => (
                        <Chip
                          key={`${tenant.id}-${code.id || code.tenant_code}`}
                          icon={<CodeIcon />}
                          label={code.tenant_code}
                          size="small"
                          color={code.is_primary ? 'primary' : 'default'}
                          variant={code.is_primary ? 'filled' : 'outlined'}
                          title={code.description || (code.is_primary ? '주 코드' : '부 코드')}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {tenant.domain && (
                      <Chip
                        icon={<DomainIcon />}
                        label={tenant.domain}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {tenant.primary_contact_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {tenant.primary_contact_email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {tenant.file_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={tenant.is_active ? '활성' : '비활성'} 
                      color={tenant.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditTenant(tenant)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteTenant(tenant)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Tenant Form Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTenant ? '테넌트 수정' : '테넌트 생성'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="테넌트명"
              value={tenantForm.tenant_name}
              onChange={(e) => setTenantForm(prev => ({ ...prev, tenant_name: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="회사명 또는 조직명"
              required
            />
            <TextField
              fullWidth
              label="설명"
              value={tenantForm.description}
              onChange={(e) => setTenantForm(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
              multiline
              rows={2}
              helperText="테넌트에 대한 추가 설명"
            />
            <TextField
              fullWidth
              label="도메인"
              value={tenantForm.domain}
              onChange={(e) => setTenantForm(prev => ({ ...prev, domain: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="회사 도메인 (예: company.com)"
            />
            <TextField
              fullWidth
              label="주요 담당자"
              value={tenantForm.primary_contact_name}
              onChange={(e) => setTenantForm(prev => ({ ...prev, primary_contact_name: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="주요 담당자 이름"
            />
            <TextField
              fullWidth
              label="주요 담당자 이메일"
              type="email"
              value={tenantForm.primary_contact_email}
              onChange={(e) => setTenantForm(prev => ({ ...prev, primary_contact_email: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="주요 담당자 이메일 주소"
            />
            <TextField
              fullWidth
              label="BSDA Time"
              value={tenantForm.bsda_time}
              onChange={(e) => setTenantForm(prev => ({ ...prev, bsda_time: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="20251231235959"
              helperText="기준일시 (YYYYMMDDHHMMSS 형식, 14자리)"
              inputProps={{ maxLength: 14 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={tenantForm.is_active}
                  onChange={(e) => setTenantForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="활성화"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" sx={{ mb: 1 }}>K8s 에이전트 (선택)</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
              <TextField
                label="아이콘 (이모지)"
                value={tenantForm.agent_emoji}
                onChange={(e) => setTenantForm(prev => ({ ...prev, agent_emoji: e.target.value }))}
                size="small"
                sx={{ width: 200 }}
                inputProps={{ maxLength: 4 }}
                helperText="모니터링 그리드에 표시"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={tenantForm.provision_agent}
                    onChange={(e) => setTenantForm(prev => ({ ...prev, provision_agent: e.target.checked }))}
                    disabled={!!editingTenant}
                  />
                }
                label={
                  editingTenant
                    ? 'K8s 에이전트 (수정 시 변경 안 됨)'
                    : '저장 시 K8s 에이전트도 함께 생성'
                }
                sx={{ mt: 1 }}
              />
            </Box>
            <TextField
              fullWidth
              label="GCS 버킷"
              value={tenantForm.agent_bucket}
              onChange={(e) => setTenantForm(prev => ({ ...prev, agent_bucket: e.target.value }))}
              sx={{ mb: 2 }}
              size="small"
              placeholder="비워두면 기본 패턴으로 자동 생성"
              helperText={
                tenantForm.agent_bucket
                  ? `사용 버킷: ${tenantForm.agent_bucket}`
                  : '기본: hyperlounge-{tenant_code}-foldersync (코드가 변경되어도 명시적으로 입력 가능)'
              }
            />
            {tenantForm.provision_agent && !editingTenant && (
              <Alert severity="info" sx={{ mb: 2 }}>
                ⚠️ K8s 에이전트 생성 전 GCS 버킷 두 개가 미리 만들어져 있어야 합니다:
                <code style={{ display: 'block', marginTop: 4 }}>
                  hyperlounge-{'{'}tenant_code{'}'}-foldersync<br />
                  hyperlounge-{'{'}tenant_code{'}'}
                </code>
                버킷이 없으면 파드가 시작되지 않습니다.
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">테넌트 코드</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddTenantCode}
              >
                코드 추가
              </Button>
            </Box>
            
            <List>
              {tenantForm.tenant_codes
                .map((code, originalIndex) => ({ code, originalIndex }))
                .filter(({ code }) => code.action !== 'delete')
                .map(({ code, originalIndex }) => (
                <ListItem key={originalIndex} sx={{ px: 0 }}>
                  <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
                    <TextField
                      label="테넌트 코드"
                      value={code.tenant_code || ''}
                      onChange={(e) => handleTenantCodeChange(originalIndex, 'tenant_code', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="예: 00000680"
                      helperText={code.is_primary ? '주 코드 (필수)' : '부 코드'}
                      required
                    />
                    <TextField
                      label="설명"
                      value={code.description || ''}
                      onChange={(e) => handleTenantCodeChange(originalIndex, 'description', e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={code.is_primary || false}
                          onChange={(e) => {
                            // 다른 코드들의 is_primary를 false로 설정
                            setTenantForm(prev => {
                              const updatedCodes = prev.tenant_codes.map((tc, idx) => ({
                                ...tc,
                                is_primary: idx === originalIndex ? e.target.checked : false,
                                action: tc.id && (tc.is_primary !== (idx === originalIndex ? e.target.checked : false)) ? 'update' : tc.action
                              }));
                              return { ...prev, tenant_codes: updatedCodes };
                            });
                          }}
                          size="small"
                        />
                      }
                      label="주"
                    />
                    <IconButton
                      onClick={() => handleRemoveTenantCode(originalIndex)}
                      size="small"
                      disabled={tenantForm.tenant_codes.filter(tc => tc.action !== 'delete').length <= 1}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button 
            onClick={handleSaveTenant} 
            variant="contained" 
            disabled={loading}
            sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {editingTenant ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantManagementPageOntology;